import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useHandDraft,
  useHandDrafts,
  useCreateDraft,
  useUpdateDraft,
  useDeleteDraft,
  useTestDraft,
  useRunEvals,
  useImprovePrompt,
  usePublishDraft,
  type HandDraft,
  type EvalCase,
} from "../../hooks/useHands";
import { useHasLlmKey } from "../../hooks/useAgents";
import { apiPatch } from "../../api/client";

// ─── Drafts List ─────────────────────────────────────────────────────────────

export function HandsDraftsList() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const draftsQ = useHandDrafts(workspaceId);
  const createMut = useCreateDraft(workspaceId);
  const deleteMut = useDeleteDraft(workspaceId);

  // Context-aware prefill from /smart-match → "+ Agent Builder" button.
  // When ?prefill_name=... is present, auto-create a draft seeded with
  // that name (and description if provided), then route into the editor.
  // Strip the params on success so back-button doesn't re-trigger.
  const prefillRanRef = useRef(false);
  useEffect(() => {
    if (prefillRanRef.current) return;
    const prefillName = searchParams.get("prefill_name");
    const prefillDesc = searchParams.get("prefill_description");
    if (!prefillName || !workspaceId) return;
    prefillRanRef.current = true;
    (async () => {
      const created = await createMut.mutateAsync();
      await apiPatch(
        `/api/workspaces/${workspaceId}/hands/builder/drafts/${created.id}`,
        {
          name: prefillName,
          ...(prefillDesc ? { description: prefillDesc } : {}),
        },
      );
      setSearchParams({}, { replace: true });
      navigate(`/workspaces/${workspaceId}/hands/builder/${created.id}`, { replace: true });
    })().catch(() => {
      prefillRanRef.current = false;
    });
  }, [searchParams, workspaceId, createMut, navigate, setSearchParams]);

  const handleNew = async () => {
    const r = await createMut.mutateAsync();
    navigate(`/workspaces/${workspaceId}/hands/builder/${r.id}`);
  };

  const handleDelete = async (e: React.MouseEvent, draftId: string) => {
    e.stopPropagation();
    if (!confirm("Delete this draft? This cannot be undone.")) return;
    deleteMut.mutate(draftId);
  };

  const drafts = draftsQ.data ?? [];

  return (
    <div className="main-inner">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <div className="greet">Builder</div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn primary"
          onClick={handleNew}
          disabled={createMut.isPending}
        >
          {createMut.isPending ? "Creating…" : "+ New Hand"}
        </button>
      </div>
      <div className="greet-sub">
        Drafts are private to you. Publish to make a hand available in your Company Library.
      </div>

      {draftsQ.isLoading ? (
        <div style={{ marginTop: 24 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ height: 56, borderRadius: 8, background: "var(--bg-soft)", marginBottom: 8, opacity: 0.6 }} />
          ))}
        </div>
      ) : drafts.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-3)" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, color: "var(--text-2)" }}>No drafts yet</div>
          <div style={{ fontSize: 12, marginBottom: 20 }}>Start building a hand from scratch or fork one from the Library.</div>
          <button type="button" className="btn primary" onClick={handleNew} disabled={createMut.isPending}>
            + New Hand
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 20 }}>
          {drafts.map((d) => (
            <div
              key={d.id}
              onClick={() => navigate(`/workspaces/${workspaceId}/hands/builder/${d.id}`)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 8, cursor: "pointer", background: "var(--bg)", transition: "background 100ms" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-soft)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--bg)")}
            >
              <div style={{ fontSize: 20 }}>{d.icon ?? "✦"}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{d.name ?? "Untitled Hand"}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                  {d.category} · {d.evals?.length ?? 0} evals · updated {new Date(d.updated_at).toLocaleDateString()}
                </div>
              </div>
              {d.published_hand_id && (
                <span className="pill green" style={{ fontSize: 10 }}>Published</span>
              )}
              <span className="pill gray" style={{ fontSize: 10 }}>{MODELS.find((m) => m.id === d.model)?.label ?? d.model}</span>
              <button
                type="button"
                className="btn sm"
                style={{ fontSize: 11, color: "#c0392b" }}
                onClick={(e) => handleDelete(e, d.id)}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Software Factory nudge ──────────────────────────────────────────────────

const SOFTWARE_AGENT_PATTERNS = [
  /\breal[- ]?time\b/i,
  /\bencounter[- ]?time\b/i,
  /\bencounter[- ]?bound\b/i,
  /\bat (the )?point of (prescri|order|dispens|admit)/i,
  /\bworklist\b/i,
  /\b(ADT|EHR|lab|med admin)[- ]?feed\b/i,
  /\bevery (order|prescription|visit|admission)\b/i,
  /\bstanding watcher\b/i,
  /\bhigh[- ]?volume[, ]+(stable|continuous|embedded)\b/i,
  /\bembedded in (real[- ]?time|clinical|encounter|order)\b/i,
  /\b(ambient scribe|dose calculator|drug interaction checker|imaging triage)\b/i,
];

function isSoftwareAgentSignal(text: string): boolean {
  return SOFTWARE_AGENT_PATTERNS.some((p) => p.test(text));
}

// ─── Constants ──────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: "identity", label: "Identity" },
  { id: "personality", label: "Personality" },
  { id: "tools", label: "Tools & Skills" },
  { id: "input", label: "Input Schema" },
  { id: "output", label: "Output Schema" },
  { id: "runtime", label: "Runtime Config" },
  { id: "evals", label: "Evals" },
  { id: "governance", label: "Governance" },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

const MODELS = [
  // Anthropic
  { id: "claude-haiku-4-5-20251001", label: "Haiku 4.5",  note: "Fast · cheapest",        provider: "Anthropic" },
  { id: "claude-sonnet-4-6",         label: "Sonnet 4.6", note: "Balanced · recommended",  provider: "Anthropic" },
  { id: "claude-opus-4-7",           label: "Opus 4.7",   note: "Most capable",            provider: "Anthropic" },
  // Google Gemini
  { id: "gemini-2.5-flash",          label: "Flash 2.5",  note: "Fast · cost-efficient",   provider: "Gemini" },
  { id: "gemini-2.5-pro",            label: "Pro 2.5",    note: "High capability",          provider: "Gemini" },
  // OpenAI
  { id: "gpt-4o-mini",               label: "4o Mini",    note: "Fast · cheapest",         provider: "OpenAI" },
  { id: "gpt-4o",                    label: "4o",         note: "Balanced · recommended",  provider: "OpenAI" },
  { id: "o3",                        label: "o3",         note: "Reasoning model",         provider: "OpenAI" },
];

const PROVIDER_GROUPS = ["Anthropic", "Gemini", "OpenAI"] as const;

const CATEGORIES = ["Sales", "Marketing", "Operations", "Finance", "Strategy", "HR", "Legal", "General"];

const CLAUDE_TOOLS = [
  { id: "web_search", label: "Web Search", desc: "Search the internet" },
  { id: "web_fetch", label: "Web Fetch", desc: "Fetch a URL" },
  { id: "bash", label: "Bash", desc: "Run shell commands" },
  { id: "code_execution", label: "Code Execution", desc: "Run Python/JS sandboxed" },
];

const XAMUN_SKILLS = [
  { id: "create_play", label: "Create Play", desc: "Kick off a new play in a workspace" },
  { id: "create_situation", label: "Create Situation", desc: "Log a situation signal" },
  { id: "notify_user", label: "Notify User", desc: "Send an in-app notification" },
  { id: "update_step", label: "Update Step", desc: "Mark a play step as done" },
  { id: "send_telegram", label: "Send Telegram", desc: "Send a Telegram message" },
  { id: "lookup_member", label: "Lookup Member", desc: "Find a workspace member" },
];

const DEFAULT_DRAFT: Partial<HandDraft> = {
  name: null,
  description: null,
  when_to_use: null,
  sample_use_cases: [],
  category: "General",
  icon: null,
  system_prompt: "",
  model: "claude-sonnet-4-6",
  allowed_tools: [],
  mcp_servers: [],
  xamun_skills: [],
  max_turns: 10,
  timeout_ms: 30000,
  permission_mode: "default",
  data_classification: "internal",
  pii_handling: "pass",
  cost_tier: "standard",
  input_schema: null,
  output_schema: null,
  structured_output: false,
  approval_state: "draft",
  evals: [],
};

// ─── Main Component ──────────────────────────────────────────────────────────

export function HandsBuilder() {
  const { workspaceId, draftId } = useParams<{ workspaceId: string; draftId?: string }>();
  const navigate = useNavigate();

  const draftQ = useHandDraft(workspaceId, draftId);
  const updateMut = useUpdateDraft(workspaceId, draftId);
  const testMut = useTestDraft(workspaceId, draftId);
  const evalsMut = useRunEvals(workspaceId, draftId);
  const improveMut = useImprovePrompt(workspaceId, draftId);
  const publishMut = usePublishDraft(workspaceId, draftId);
  const llmKey = useHasLlmKey(workspaceId ?? "");
  // Test, eval-run, and improve-prompt all require a configured company LLM
  // key — the API returns 422 no_llm_key_configured otherwise. We disable
  // the buttons up-front so the user gets a clear signal instead of hitting
  // an error toast after clicking.
  const runsAreInactive = !llmKey.isLoading && !llmKey.hasDefault;

  const [activeSection, setActiveSection] = useState<SectionId>("identity");
  const [form, setForm] = useState<Partial<HandDraft>>(DEFAULT_DRAFT);
  const [testInput, setTestInput] = useState("{}");
  const [testOutput, setTestOutput] = useState<string | null>(null);
  const [testTelemetry, setTestTelemetry] = useState<Record<string, unknown> | null>(null);
  const [showPublish, setShowPublish] = useState(false);
  const [publishVersion, setPublishVersion] = useState("1.0.0");
  const [saved, setSaved] = useState(true);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  const showSoftwareNudge =
    !nudgeDismissed &&
    isSoftwareAgentSignal(`${form.description ?? ""} ${form.when_to_use ?? ""} ${form.system_prompt ?? ""}`);

  // Initialise form once draft loads; normalize JSONB fields that pg may return as {} instead of []
  useEffect(() => {
    if (draftQ.data) {
      const d = draftQ.data;
      setForm({
        ...d,
        allowed_tools: Array.isArray(d.allowed_tools) ? d.allowed_tools : [],
        mcp_servers: Array.isArray(d.mcp_servers) ? d.mcp_servers : [],
        xamun_skills: Array.isArray(d.xamun_skills) ? d.xamun_skills : [],
        evals: Array.isArray(d.evals) ? d.evals : [],
      });
    }
  }, [draftQ.data]);

  const patch = useCallback((updates: Partial<HandDraft>) => {
    setForm((prev) => ({ ...prev, ...updates }));
    setSaved(false);
  }, []);

  const handleSave = async () => {
    if (!draftId || draftId === "new") return;
    await updateMut.mutateAsync(form);
    setSaved(true);
  };

  const handleTest = async () => {
    if (!draftId || draftId === "new") return;
    await handleSave();
    let parsed: Record<string, unknown> = {};
    try { parsed = JSON.parse(testInput); } catch { /* ignore */ }
    const r = await testMut.mutateAsync(parsed);
    setTestOutput(r.output);
    setTestTelemetry(r.telemetry as Record<string, unknown>);
  };

  const handleRunEvals = async () => {
    await handleSave();
    const r = await evalsMut.mutateAsync();
    // Refresh draft to get updated eval results
    setForm((prev) => ({
      ...prev,
      evals: prev.evals?.map((ev) => {
        const res = r.results.find((x) => x.id === ev.id);
        return res ? { ...ev, last_result: res.result } : ev;
      }) ?? [],
    }));
  };

  const handleImprove = async () => {
    await handleSave();
    const r = await improveMut.mutateAsync();
    patch({ system_prompt: r.improved_prompt });
  };

  const handlePublish = async () => {
    const r = await publishMut.mutateAsync(publishVersion);
    setShowPublish(false);
    navigate(`/workspaces/${workspaceId}/hands/library`);
    void r;
  };

  const evalsPass =
    (form.evals?.length ?? 0) >= 3 &&
    (form.evals?.filter((e) => e.last_result === "pass").length ?? 0) /
      Math.max(form.evals?.length ?? 1, 1) >= 0.8;

  if (draftQ.isLoading) {
    return <div className="main-inner" style={{ color: "var(--text-3)", paddingTop: 40 }}>Loading draft…</div>;
  }

  return (
    <div className="builder-wrap">
      {/* Top bar */}
      <div className="bbar">
        <button
          type="button"
          className="bbar-back"
          onClick={() => navigate(`/workspaces/${workspaceId}/hands/library`)}
        >
          ← Library
        </button>
        <span className="bbar-title">{form.name ?? "Untitled Hand"}</span>
        <div style={{ flex: 1 }} />
        <span
          className="pill"
          style={{
            background: evalsPass ? "#e4f4ec" : "#fff3e0",
            color: evalsPass ? "#2e7d4f" : "#b76c00",
            marginRight: 8,
          }}
        >
          {evalsPass ? "✓ Eval gate" : "⚠ Eval gate"}
        </span>
        <button
          type="button"
          className="btn sm"
          onClick={handleSave}
          disabled={saved || updateMut.isPending}
        >
          {updateMut.isPending ? "Saving…" : saved ? "Saved" : "Save"}
        </button>
        <button
          type="button"
          className="btn primary sm"
          onClick={() => setShowPublish(true)}
          style={{ marginLeft: 6 }}
        >
          Publish
        </button>
      </div>

      {/* Section nav */}
      <div className="bsec">
        {/* Inactive state — agent runs require a company LLM key. Sits at
            the top of the section-nav column as a compact menu-style item
            so it lives next to the actions it gates (Run, Improve) rather
            than floating over the page. */}
        {runsAreInactive && (
          <div
            data-testid="hb-no-llm-banner"
            style={{
              margin: "0 0 8px",
              padding: "8px 10px",
              borderRadius: 6,
              background: "color-mix(in srgb, var(--danger, #d9534f) 8%, transparent)",
              border: "1px solid color-mix(in srgb, var(--danger, #d9534f) 30%, transparent)",
              fontSize: 11,
              lineHeight: 1.4,
              color: "var(--text-2)",
            }}
          >
            <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>
              ⚠ No LLM key
            </div>
            <div>
              <em>Run</em> and <em>Improve</em> are off. Add a key under{" "}
              <strong>Hands Agents → Integration &amp; LLM</strong>.
            </div>
          </div>
        )}
        {SECTIONS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={`bsec-item ${activeSection === s.id ? "active" : ""}`}
            onClick={() => setActiveSection(s.id)}
          >
            <span className="bsec-num">{i + 1}</span>
            {s.label}
          </button>
        ))}
      </div>

      {/* Software Factory nudge */}
      {showSoftwareNudge && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          background: "#fffbeb", border: "1px solid #f59e0b",
          borderRadius: 8, padding: "10px 14px", marginBottom: 12,
          fontSize: 13, color: "#92400e",
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚡</span>
          <div style={{ flex: 1 }}>
            <strong>This looks like a real-time Software agent.</strong>
            {" "}Hands are episodic plays that produce a deliverable on demand.
            Real-time, encounter-bound, or high-volume embedded agents belong in{" "}
            <strong>Software Factory</strong> — not the Hands Library.
          </div>
          <button
            type="button"
            onClick={() => setNudgeDismissed(true)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#92400e", fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 }}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {/* Form */}
      <div className="bform">
        {activeSection === "identity" && (
          <IdentitySection form={form} patch={patch} />
        )}
        {activeSection === "personality" && (
          <PersonalitySection
            form={form}
            patch={patch}
            onImprove={handleImprove}
            improving={improveMut.isPending}
            improveDisabled={runsAreInactive}
          />
        )}
        {activeSection === "tools" && (
          <ToolsSection form={form} patch={patch} />
        )}
        {activeSection === "input" && (
          <SchemaSection
            title="Input Schema"
            value={form.input_schema ?? null}
            onChange={(v) => patch({ input_schema: v })}
          />
        )}
        {activeSection === "output" && (
          <SchemaSection
            title="Output Schema"
            value={form.output_schema ?? null}
            onChange={(v) => patch({ output_schema: v })}
            structuredOutput={form.structured_output ?? false}
            onToggleStructured={(v) => patch({ structured_output: v })}
          />
        )}
        {activeSection === "runtime" && (
          <RuntimeSection form={form} patch={patch} />
        )}
        {activeSection === "evals" && (
          <EvalsSection
            form={form}
            patch={patch}
            onRun={handleRunEvals}
            running={evalsMut.isPending}
          />
        )}
        {activeSection === "governance" && (
          <GovernanceSection form={form} patch={patch} />
        )}
      </div>

      {/* Test panel */}
      <div className="btest">
        <div className="btest-hd">Test</div>
        <div className="btest-body">
          <div className="bform-label">Input (JSON)</div>
          <textarea
            className="input"
            style={{ fontFamily: "monospace", fontSize: 11, resize: "vertical", minHeight: 80 }}
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            placeholder='{"key": "value"}'
          />
          {testOutput !== null && (
            <>
              <div className="bform-label" style={{ marginTop: 12 }}>Output</div>
              <div className="btest-output">{testOutput}</div>
            </>
          )}
          {testTelemetry !== null && (
            <div className="btest-telemetry">
              <span>{(testTelemetry.latency_ms as number)?.toLocaleString() ?? "—"}ms</span>
              <span>{(testTelemetry.tokens_in as number) ?? 0}↑ {(testTelemetry.tokens_out as number) ?? 0}↓ tokens</span>
              <span>${((testTelemetry.cost_usd as number) ?? 0).toFixed(4)}</span>
            </div>
          )}
        </div>
        <div className="btest-foot">
          <button
            type="button"
            className="btn primary"
            style={{ width: "100%" }}
            onClick={handleTest}
            disabled={
              testMut.isPending ||
              !draftId ||
              draftId === "new" ||
              runsAreInactive
            }
            title={runsAreInactive ? "No LLM key configured for this company" : undefined}
            data-testid="hb-run-btn"
          >
            {testMut.isPending
              ? "Running…"
              : runsAreInactive
                ? "Run · no key"
                : "Run"}
          </button>
        </div>
      </div>

      {/* Publish modal */}
      {showPublish && (
        <PublishModal
          form={form}
          evalsPass={evalsPass}
          version={publishVersion}
          onVersionChange={setPublishVersion}
          onPublish={handlePublish}
          onClose={() => setShowPublish(false)}
          publishing={publishMut.isPending}
          error={publishMut.error instanceof Error ? publishMut.error.message : null}
        />
      )}
    </div>
  );
}

// ─── Section: Identity helpers ───────────────────────────────────────────────

const MAX_USE_CASES = 5;
const MAX_USE_CASE_LEN = 200;

/**
 * Small list editor for sample_use_cases. Capped at 5 entries × 200 chars.
 * Drives both the library card's "use case examples" hint and the smart
 * match scorer's high-weight token source.
 */
function SampleUseCasesEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const add = () => {
    if (value.length >= MAX_USE_CASES) return;
    onChange([...value, ""]);
  };
  const update = (i: number, v: string) => {
    const next = value.slice();
    next[i] = v.slice(0, MAX_USE_CASE_LEN);
    onChange(next);
  };
  const remove = (i: number) => {
    onChange(value.filter((_, j) => j !== i));
  };
  const trimmed = value.filter((v) => v.trim().length > 0);
  return (
    <>
      <div className="bform-label">
        Sample use cases
        <span style={{ marginLeft: 8, color: "var(--text-3)", fontWeight: 400, fontSize: 11 }}>
          Concrete examples — shown in the library and used by Smart Match to rank this agent. Max {MAX_USE_CASES}.
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {value.map((entry, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              className="input"
              style={{ flex: 1 }}
              placeholder={`e.g. Run weekly cohort retention analysis`}
              value={entry}
              onChange={(e) => update(i, e.target.value)}
              maxLength={MAX_USE_CASE_LEN}
            />
            <button
              type="button"
              className="btn sm"
              style={{ color: "#c0392b" }}
              onClick={() => remove(i)}
              aria-label="Remove sample use case"
            >
              ×
            </button>
          </div>
        ))}
        {value.length < MAX_USE_CASES ? (
          <button
            type="button"
            className="btn sm"
            style={{ alignSelf: "flex-start", fontSize: 12 }}
            onClick={add}
          >
            + Add sample use case
          </button>
        ) : (
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>
            Max {MAX_USE_CASES} reached.
          </div>
        )}
        {trimmed.length > 0 ? (
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>
            {trimmed.length} of {MAX_USE_CASES} used.
          </div>
        ) : null}
      </div>
    </>
  );
}

// ─── Section: Identity ───────────────────────────────────────────────────────

function IdentitySection({
  form,
  patch,
}: {
  form: Partial<HandDraft>;
  patch: (u: Partial<HandDraft>) => void;
}) {
  return (
    <div>
      <div className="bform-section-title">Identity</div>
      <div className="bform-label">Name *</div>
      <input
        className="input"
        style={{ width: "100%" }}
        placeholder="e.g. Sales Prospector"
        value={form.name ?? ""}
        onChange={(e) => patch({ name: e.target.value || null })}
      />
      <div className="bform-label">Description</div>
      <textarea
        className="input"
        style={{ width: "100%", resize: "vertical", minHeight: 60 }}
        placeholder="What does this agent do?"
        value={form.description ?? ""}
        onChange={(e) => patch({ description: e.target.value || null })}
      />
      <div className="bform-label">When to use</div>
      <textarea
        className="input"
        style={{ width: "100%", resize: "vertical", minHeight: 60 }}
        placeholder="Describe the ideal trigger — shown in the library to help users decide."
        value={form.when_to_use ?? ""}
        onChange={(e) => patch({ when_to_use: e.target.value || null })}
      />
      <SampleUseCasesEditor
        value={form.sample_use_cases ?? []}
        onChange={(next) => patch({ sample_use_cases: next })}
      />
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="bform-label">Icon (emoji)</div>
          <input
            className="input"
            style={{ width: "100%" }}
            placeholder="✦"
            value={form.icon ?? ""}
            onChange={(e) => patch({ icon: e.target.value || null })}
          />
        </div>
        <div style={{ flex: 2 }}>
          <div className="bform-label">Category</div>
          <select
            className="select"
            style={{ width: "100%" }}
            value={form.category ?? "General"}
            onChange={(e) => patch({ category: e.target.value })}
          >
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Personality ────────────────────────────────────────────────────

function PersonalitySection({
  form,
  patch,
  onImprove,
  improving,
  improveDisabled,
}: {
  form: Partial<HandDraft>;
  patch: (u: Partial<HandDraft>) => void;
  onImprove: () => void;
  improving: boolean;
  improveDisabled: boolean;
}) {
  return (
    <div>
      <div className="bform-section-title">Personality</div>
      <div className="bform-label">Model</div>
      {PROVIDER_GROUPS.map((provider) => (
        <div key={provider} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.08em", marginBottom: 4 }}>
            {provider.toUpperCase()}
          </div>
          <div className="model-picker">
            {MODELS.filter((m) => m.provider === provider).map((m) => (
              <button
                key={m.id}
                type="button"
                className={`model-card ${form.model === m.id ? "active" : ""}`}
                onClick={() => patch({ model: m.id })}
              >
                <div style={{ fontWeight: 600, fontSize: 12 }}>{m.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{m.note}</div>
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="improve-bar">
        <div className="bform-label" style={{ margin: 0 }}>System Prompt *</div>
        <button
          type="button"
          className="btn sm"
          onClick={onImprove}
          disabled={improving || !form.system_prompt || improveDisabled}
          title={improveDisabled ? "No LLM key configured for this company" : undefined}
          data-testid="hb-improve-btn"
        >
          {improving ? "Improving…" : improveDisabled ? "✨ Improve · no key" : "✨ Improve"}
        </button>
      </div>
      <textarea
        className="input"
        style={{ width: "100%", resize: "vertical", minHeight: 240, fontFamily: "monospace", fontSize: 12, lineHeight: 1.6 }}
        placeholder="You are a helpful assistant that…"
        value={form.system_prompt ?? ""}
        onChange={(e) => patch({ system_prompt: e.target.value })}
      />
    </div>
  );
}

// ─── Section: Tools & Skills ─────────────────────────────────────────────────

function ToolsSection({
  form,
  patch,
}: {
  form: Partial<HandDraft>;
  patch: (u: Partial<HandDraft>) => void;
}) {
  const [tab, setTab] = useState<"claude" | "mcp" | "xamun">("claude");

  const toggleTool = (id: string) => {
    const cur = form.allowed_tools ?? [];
    patch({ allowed_tools: cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id] });
  };

  const toggleSkill = (id: string) => {
    const cur = form.xamun_skills ?? [];
    patch({ xamun_skills: cur.includes(id) ? cur.filter((s) => s !== id) : [...cur, id] });
  };

  return (
    <div>
      <div className="bform-section-title">Tools &amp; Skills</div>
      <div className="tool-picker">
        <div className="tp-tabs">
          {(["claude", "mcp", "xamun"] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`tp-tab ${tab === t ? "active" : ""}`}
              onClick={() => setTab(t)}
            >
              {t === "claude" ? "Claude Built-ins" : t === "mcp" ? "MCP Servers" : "Xamun Skills"}
            </button>
          ))}
        </div>
        <div className="tp-body">
          {tab === "claude" && (
            <div className="tp-col">
              {CLAUDE_TOOLS.map((tool) => (
                <label key={tool.id} className="tool-item">
                  <input
                    type="checkbox"
                    checked={(form.allowed_tools ?? []).includes(tool.id)}
                    onChange={() => toggleTool(tool.id)}
                  />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 12 }}>{tool.label}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>{tool.desc}</div>
                  </div>
                  <span className="tool-badge">claude</span>
                </label>
              ))}
            </div>
          )}
          {tab === "mcp" && (
            <div className="tp-col">
              <div style={{ fontSize: 12, color: "var(--text-3)", padding: "20px 0" }}>
                MCP server bindings come from your Capability Setup. Connect integrations (Salesforce, HubSpot, etc.) there first, then they appear here.
              </div>
              {(form.mcp_servers ?? []).length > 0 && (
                <div>
                  {form.mcp_servers!.map((s) => (
                    <label key={s.id} className="tool-item">
                      <input
                        type="checkbox"
                        checked
                        onChange={() =>
                          patch({ mcp_servers: form.mcp_servers!.filter((x) => x.id !== s.id) })
                        }
                      />
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{s.id}</div>
                      <span className="tool-badge">mcp</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          {tab === "xamun" && (
            <div className="tp-col">
              {XAMUN_SKILLS.map((skill) => (
                <label key={skill.id} className="tool-item">
                  <input
                    type="checkbox"
                    checked={(form.xamun_skills ?? []).includes(skill.id)}
                    onChange={() => toggleSkill(skill.id)}
                  />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 12 }}>{skill.label}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>{skill.desc}</div>
                  </div>
                  <span className="tool-badge xamun">xamun</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Section: Schema ─────────────────────────────────────────────────────────

function SchemaSection({
  title,
  value,
  onChange,
  structuredOutput,
  onToggleStructured,
}: {
  title: string;
  value: Record<string, unknown> | null;
  onChange: (v: Record<string, unknown> | null) => void;
  structuredOutput?: boolean;
  onToggleStructured?: (v: boolean) => void;
}) {
  const [raw, setRaw] = useState(value ? JSON.stringify(value, null, 2) : "");
  const [err, setErr] = useState<string | null>(null);

  const handleChange = (text: string) => {
    setRaw(text);
    if (!text.trim()) { onChange(null); setErr(null); return; }
    try {
      onChange(JSON.parse(text));
      setErr(null);
    } catch {
      setErr("Invalid JSON");
    }
  };

  return (
    <div>
      <div className="bform-section-title">{title}</div>
      {onToggleStructured !== undefined && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={!!structuredOutput}
            onChange={(e) => onToggleStructured(e.target.checked)}
          />
          Enforce structured output (model must return valid JSON matching schema)
        </label>
      )}
      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8 }}>
        Define as JSON Schema (leave blank for unstructured I/O).
      </div>
      <textarea
        className={`input ${err ? "error" : ""}`}
        style={{ width: "100%", minHeight: 260, fontFamily: "monospace", fontSize: 11, lineHeight: 1.6, resize: "vertical" }}
        value={raw}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={'{\n  "type": "object",\n  "properties": {\n    "result": { "type": "string" }\n  }\n}'}
      />
      {err && <div style={{ fontSize: 11, color: "#c0392b", marginTop: 4 }}>{err}</div>}
    </div>
  );
}

// ─── Section: Runtime Config ─────────────────────────────────────────────────

function RuntimeSection({
  form,
  patch,
}: {
  form: Partial<HandDraft>;
  patch: (u: Partial<HandDraft>) => void;
}) {
  return (
    <div>
      <div className="bform-section-title">Runtime Config</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <div className="bform-label">Max turns</div>
          <input
            type="number"
            className="input"
            style={{ width: "100%" }}
            min={1}
            max={50}
            value={form.max_turns ?? 10}
            onChange={(e) => patch({ max_turns: Number(e.target.value) })}
          />
        </div>
        <div>
          <div className="bform-label">Timeout (ms)</div>
          <input
            type="number"
            className="input"
            style={{ width: "100%" }}
            min={5000}
            step={5000}
            value={form.timeout_ms ?? 30000}
            onChange={(e) => patch({ timeout_ms: Number(e.target.value) })}
          />
        </div>
        <div>
          <div className="bform-label">Permission mode</div>
          <select
            className="select"
            style={{ width: "100%" }}
            value={form.permission_mode ?? "default"}
            onChange={(e) => patch({ permission_mode: e.target.value })}
          >
            <option value="default">Default</option>
            <option value="bypassPermissions">Bypass Permissions</option>
          </select>
        </div>
        <div>
          <div className="bform-label">Cost tier</div>
          <select
            className="select"
            style={{ width: "100%" }}
            value={form.cost_tier ?? "standard"}
            onChange={(e) => patch({ cost_tier: e.target.value })}
          >
            <option value="micro">Micro</option>
            <option value="standard">Standard</option>
            <option value="pro">Pro</option>
          </select>
        </div>
        <div>
          <div className="bform-label">Data classification</div>
          <select
            className="select"
            style={{ width: "100%" }}
            value={form.data_classification ?? "internal"}
            onChange={(e) => patch({ data_classification: e.target.value })}
          >
            <option value="public">Public</option>
            <option value="internal">Internal</option>
            <option value="confidential">Confidential</option>
            <option value="restricted">Restricted</option>
          </select>
        </div>
        <div>
          <div className="bform-label">PII handling</div>
          <select
            className="select"
            style={{ width: "100%" }}
            value={form.pii_handling ?? "pass"}
            onChange={(e) => patch({ pii_handling: e.target.value })}
          >
            <option value="pass">Pass (allow PII through)</option>
            <option value="redact">Redact (strip PII)</option>
            <option value="block">Block (reject if PII detected)</option>
          </select>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Evals ──────────────────────────────────────────────────────────

function EvalsSection({
  form,
  patch,
  onRun,
  running,
}: {
  form: Partial<HandDraft>;
  patch: (u: Partial<HandDraft>) => void;
  onRun: () => void;
  running: boolean;
}) {
  const evals = form.evals ?? [];
  const passed = evals.filter((e) => e.last_result === "pass").length;
  const passRate = evals.length > 0 ? Math.round((passed / evals.length) * 100) : null;

  const addCase = () => {
    const newCase: EvalCase = {
      id: crypto.randomUUID(),
      title: `Case ${evals.length + 1}`,
      input: {},
      expected: {},
    };
    patch({ evals: [...evals, newCase] });
  };

  const updateCase = (id: string, updates: Partial<EvalCase>) => {
    patch({ evals: evals.map((e) => (e.id === id ? { ...e, ...updates } : e)) });
  };

  const removeCase = (id: string) => {
    patch({ evals: evals.filter((e) => e.id !== id) });
  };

  return (
    <div>
      <div className="eval-hd">
        <div className="bform-section-title" style={{ margin: 0 }}>Evals</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {passRate !== null && (
            <span
              className="eval-score"
              style={{ background: passRate >= 80 ? "#e4f4ec" : "#fff3e0", color: passRate >= 80 ? "#2e7d4f" : "#b76c00" }}
            >
              {passRate}% ({passed}/{evals.length})
            </span>
          )}
          <button
            type="button"
            className="btn sm"
            onClick={onRun}
            disabled={running || evals.length === 0}
          >
            {running ? "Running…" : "Run all"}
          </button>
          <button type="button" className="btn primary sm" onClick={addCase}>
            + Add case
          </button>
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 12 }}>
        Need ≥3 cases and ≥80% pass rate to publish. Evals run on Xamun-managed credentials.
      </div>
      {evals.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-3)", fontSize: 13 }}>
          No eval cases yet. Add at least 3 to unlock publishing.
        </div>
      ) : (
        evals.map((ev) => (
          <EvalCaseCard
            key={ev.id}
            evalCase={ev}
            onUpdate={(u) => updateCase(ev.id, u)}
            onRemove={() => removeCase(ev.id)}
          />
        ))
      )}
    </div>
  );
}

function EvalCaseCard({
  evalCase,
  onUpdate,
  onRemove,
}: {
  evalCase: EvalCase;
  onUpdate: (u: Partial<EvalCase>) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const resultColor =
    evalCase.last_result === "pass"
      ? "#2e7d4f"
      : evalCase.last_result === "fail"
      ? "#c0392b"
      : "var(--text-3)";
  const resultLabel =
    evalCase.last_result === "pass" ? "✓ Pass" : evalCase.last_result === "fail" ? "✗ Fail" : "—";

  return (
    <div className="eval-case">
      <div className="eval-case-hd" onClick={() => setOpen((o) => !o)}>
        <span style={{ fontWeight: 500, fontSize: 12 }}>{evalCase.title}</span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: resultColor }}>{resultLabel}</span>
          <button
            type="button"
            style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: 11 }}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
          >
            Remove
          </button>
          <span style={{ color: "var(--text-3)", fontSize: 11 }}>{open ? "▲" : "▼"}</span>
        </div>
      </div>
      {open && (
        <div className="eval-case-body">
          <div className="bform-label">Title</div>
          <input
            className="input"
            style={{ width: "100%" }}
            value={evalCase.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
          />
          <div className="bform-label">Input (JSON)</div>
          <textarea
            className="input eval-code"
            value={JSON.stringify(evalCase.input, null, 2)}
            onChange={(e) => {
              try { onUpdate({ input: JSON.parse(e.target.value) }); } catch { /* ignore */ }
            }}
          />
          <div className="bform-label">Expected output (JSON, subset match)</div>
          <textarea
            className="input eval-code"
            value={JSON.stringify(evalCase.expected ?? {}, null, 2)}
            onChange={(e) => {
              try { onUpdate({ expected: JSON.parse(e.target.value) }); } catch { /* ignore */ }
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Section: Governance ─────────────────────────────────────────────────────

function GovernanceSection({
  form,
  patch,
}: {
  form: Partial<HandDraft>;
  patch: (u: Partial<HandDraft>) => void;
}) {
  return (
    <div>
      <div className="bform-section-title">Governance</div>
      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 16, lineHeight: 1.6 }}>
        Governance settings control who can install this hand, whether approval is required before actions, and audit log retention.
      </div>
      <div className="bform-label">Approval state</div>
      <select
        className="select"
        style={{ width: "100%" }}
        value={form.approval_state ?? "draft"}
        onChange={(e) => patch({ approval_state: e.target.value })}
      >
        <option value="draft">Draft (not visible in library)</option>
        <option value="pending">Pending approval</option>
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
      </select>
      <div style={{ marginTop: 20, padding: 16, background: "var(--bg-soft)", borderRadius: 8, fontSize: 12, color: "var(--text-2)" }}>
        <b>Tip:</b> Publishing always sets approval_state to <code>approved</code> for company-scoped hands, or <code>pending</code> for public submissions requiring Xamun review.
      </div>
    </div>
  );
}

// ─── Publish Modal ───────────────────────────────────────────────────────────

function PublishModal({
  form,
  evalsPass,
  version,
  onVersionChange,
  onPublish,
  onClose,
  publishing,
  error,
}: {
  form: Partial<HandDraft>;
  evalsPass: boolean;
  version: string;
  onVersionChange: (v: string) => void;
  onPublish: () => void;
  onClose: () => void;
  publishing: boolean;
  error: string | null;
}) {
  const checks = [
    { label: "Name set", ok: !!form.name },
    { label: "System prompt written", ok: !!form.system_prompt },
    { label: "≥3 eval cases", ok: (form.evals?.length ?? 0) >= 3 },
    { label: "≥80% eval pass rate", ok: evalsPass },
  ];
  const allOk = checks.every((c) => c.ok);

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={onClose}
    >
      <div
        style={{ background: "var(--bg)", borderRadius: 12, padding: 28, width: 400, boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Publish Hand</div>
        <div style={{ marginBottom: 16 }}>
          {checks.map((c) => (
            <div key={c.label} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, marginBottom: 8 }}>
              <span style={{ color: c.ok ? "#2e7d4f" : "#c0392b", fontWeight: 600 }}>{c.ok ? "✓" : "✗"}</span>
              <span style={{ color: c.ok ? "var(--text-1)" : "var(--text-3)" }}>{c.label}</span>
            </div>
          ))}
        </div>
        <div className="bform-label">Version</div>
        <input
          className="input"
          style={{ width: "100%", marginBottom: 16 }}
          value={version}
          onChange={(e) => onVersionChange(e.target.value)}
          placeholder="1.0.0"
        />
        {error && (
          <div style={{ fontSize: 12, color: "#c0392b", marginBottom: 12 }}>{error}</div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn" style={{ flex: 1 }} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            style={{ flex: 1 }}
            onClick={onPublish}
            disabled={!allOk || publishing}
          >
            {publishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
